import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import ApplyPage from './pages/ApplyPage';
import CheckoutPage from './pages/CheckoutPage';
import LegalPage from './pages/LegalPages';
import InfoPage from './pages/InfoPages';
import AdminDashboard from './pages/AdminDashboard';
import ErrorPage from './pages/ErrorPage';
import CookieConsentBanner from './components/CookieConsentBanner';

type LegalTopic = 'privacy' | 'terms' | 'cookies' | 'acceptable-use' | 'disclaimer' | 'copyright' | 'gdpr' | 'data-retention' | 'dpa';
type InfoTopic = 'about' | 'contact' | 'faq' | 'blog' | 'help';
type View = 'landing' | 'auth' | 'checkout' | 'legal' | 'info' | 'admin' | 'error';

function getApplySlug(): string | null {
  const path = window.location.pathname;
  const match = path.match(/^\/apply\/([^/]+)/);
  return match ? match[1] : null;
}

function getQueryParam(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

function Router() {
  const { session, agency, loading } = useAuth();
  const [applySlug] = useState<string | null>(getApplySlug);
  const [view, setView] = useState<View>('landing');
  const [legalTopic, setLegalTopic] = useState<LegalTopic>('privacy');
  const [infoTopic, setInfoTopic] = useState<InfoTopic>('about');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingAgencyName, setPendingAgencyName] = useState<string | null>(null);
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<404 | 500>(404);
  const [initialTab, setInitialTab] = useState<string | null>(null);

  // Handle post-checkout redirect: ?payment=success&email=...&agency=...
  useEffect(() => {
    const paymentStatus = getQueryParam('payment');
    const email = getQueryParam('email');
    const agencyName = getQueryParam('agency');

    const pw = getQueryParam('pw');
    if (paymentStatus === 'success' && email) {
      setPendingEmail(email);
      setPendingAgencyName(agencyName);
      if (pw) setPendingPassword(pw);
      setView('auth');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Listen for navigation events from legal links
  useEffect(() => {
    function handleNavigate(e: Event) {
      const detail = (e as CustomEvent).detail;
      const legalTopics: LegalTopic[] = ['privacy', 'terms', 'cookies', 'acceptable-use', 'disclaimer', 'copyright', 'gdpr', 'data-retention', 'dpa'];
      const infoTopics: InfoTopic[] = ['about', 'contact', 'faq', 'blog', 'help'];

      if (legalTopics.includes(detail as LegalTopic)) {
        setLegalTopic(detail as LegalTopic);
        setView('legal');
      } else if (infoTopics.includes(detail as InfoTopic)) {
        setInfoTopic(detail as InfoTopic);
        setView('info');
      }
    }

    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  // If URL is an apply page, always show it regardless of auth state
  if (applySlug) {
    return <ApplyPage slug={applySlug} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Authenticated → go to agency dashboard
  if (session && agency && view !== 'legal' && view !== 'info' && view !== 'admin') {
    return <DashboardPage initialTab={initialTab} />;
  }

  if (session && !agency && view !== 'legal' && view !== 'info' && view !== 'admin' && view !== 'auth') {
    return <DashboardPage initialTab={initialTab} />;
  }

  // Error page
  if (view === 'error') {
    return <ErrorPage code={errorCode} onBack={() => setView('landing')} />;
  }

  // Admin dashboard
  if (view === 'admin') {
    return <AdminDashboard onBack={() => setView('landing')} />;
  }

  // Legal pages
  if (view === 'legal') {
    return <LegalPage topic={legalTopic} onBack={() => setView('landing')} />;
  }

  // Info pages
  if (view === 'info') {
    return <InfoPage page={infoTopic} onBack={() => setView('landing')} onNavigate={(p) => { setInfoTopic(p); }} />;
  }

  // Checkout page
  if (view === 'checkout') {
    return (
      <CheckoutPage
        onSuccess={(checkoutEmail: string, checkoutAgency: string, checkoutPassword: string) => {
          if (checkoutEmail) setPendingEmail(checkoutEmail);
          if (checkoutAgency) setPendingAgencyName(checkoutAgency);
          if (checkoutPassword) setPendingPassword(checkoutPassword);
          setView('auth');
        }}
        onBack={() => setView('landing')}
      />
    );
  }

  // Auth page
  if (view === 'auth') {
    return (
      <AuthPage
        onSuccess={() => setView('landing')}
        pendingEmail={pendingEmail}
        pendingAgencyName={pendingAgencyName}
        pendingPassword={pendingPassword}
        onGoCheckout={() => setView('checkout')}
        onBack={() => setView('landing')}
      />
    );
  }

  return (
    <LandingPage
      onGetStarted={() => setView('checkout')}
      onNavigateInfo={(page: InfoTopic) => { setInfoTopic(page); setView('info'); }}
      onNavigateLegal={(topic: LegalTopic) => { setLegalTopic(topic); setView('legal'); }}
      onNavigateAdmin={() => setView('admin')}
      onManageSubscription={() => {
        if (session && agency) {
          setInitialTab('subscription');
        } else {
          setView('auth');
        }
      }}
    />
  );
}

export default function App() {
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('carehire-darkmode');
  }, []);

  return (
    <AuthProvider>
      <Router />
      <CookieConsentBanner />
    </AuthProvider>
  );
}
